import { $api } from '../../services/service';
import React, { useEffect, useState } from "react";
import { Tabs, Table, Button, Modal, Form, Input, message, Popconfirm, Select, Badge } from 'antd';
import DefaultLayout from '../../layouts/DefaultLayout/DefaultLayout';
import ErrorDialog from '../../components/Elements/ErrorDialog/ErrorDialog';
import { DeleteOutlined, EditOutlined, EyeOutlined } from '@ant-design/icons';
import { DatePicker, ConfigProvider } from "antd";
import viVN from "antd/lib/locale/vi_VN";
import dayjs from "dayjs";
import "dayjs/locale/vi";
import fileDownload from 'js-file-download';
import moment from 'moment/moment';
import { _AUTH } from '../../constants/_auth';

const { TabPane } = Tabs;
const { Option } = Select;

interface Evaluation {
    id?: string;
    name: string;
    description: string;
    type: number | null;
    score: number;
}

interface TeacherEvaluation {
    teacherId: string;
    teacherName: string;
    subjectTeachingId: string;
    subjectTeachingName: string;
    subjectName: string;
    avgFourPoint: number;
    avgThreePoint: number;
    avgTwoPoint: number;
    avgOnePoint: number;
    avg: number;
    evaluationDetails: {
        evaluationName: string;
        fourPointCount: number;
        threePointCount: number;
        twoPointCount: number;
        onePointCount: number;
        avg: number;
    }[];
    studentComments: string[];
    studentCount: number;
    evaluationCount: number;
}

const EvaluationPage: React.FC = () => {
    let role = localStorage.getItem(_AUTH.ROLE) !== null && localStorage.getItem(_AUTH.ROLE) != 'Teacher' && localStorage.getItem(_AUTH.ROLE) != 'TeacherStaff';
    const [activeTab, setActiveTab] = useState("criteria");
    const [loading, setLoading] = useState(false);
    const [criteriaData, setCriteriaData] = useState<Evaluation[]>([]);
    const [totalRecord, setTotalRecord] = useState(0);
    const [pageIndex, setPageIndex] = useState(1);
    const [pageSize, setPageSize] = useState(15);
    const [filterType, setFilterType] = useState<number | null>(null);


    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingItem, setEditingItem] = useState<Evaluation | null>(null);
    const [form] = Form.useForm();
    const [errDialog, setErrDialog] = useState({ isOpen: false, subTitle: '' });

    // GPA
    const [teachers, setTeachers] = useState([]);
    const [teacherData, setTeacherData] = useState<TeacherEvaluation[]>([]);
    const [teacherLoading, setTeacherLoading] = useState(false);
    const [selectedTeacher, setSelectedTeacher] = useState<string | null>(null);
    const [startDate, setStartDate] = useState<string | null>(null);
    const [selectedYear, setSelectedYear] = useState<string | null>(null);
    const [selectedSemester, setSelectedSemester] = useState<number | null>(null);

    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [detailRecord, setDetailRecord] = useState<TeacherEvaluation | null>(null);

    const [exporting, setExporting] = useState(false);

    const fetchCriteria = async (page = pageIndex, size = pageSize, type = filterType) => {
        try {
            setLoading(true);
            const res = await $api.evaluation.getPaging(
                {
                    pageIndex: page,
                    pageSize: size,
                    type: type,
                }
            );
            if (res.result) {
                setCriteriaData(res.result.items || []);
                setTotalRecord(res.result.totalRecord);
            }
        } catch (error: any) {
            message.error("Lỗi tải dữ liệu");
        } finally {
            setLoading(false);
        }
    };

    const fetchTeacherEvaluations = async () => {
        try {
            if(teachers.length === 0) {
                const resTeachers = await $api.teacher.getAll();
                if(resTeachers.result && resTeachers.result.length > 0) {
                    const sortedTeachers = [...resTeachers.result].sort((a, b) =>
                        a.fullName.localeCompare(b.fullName, 'vi', { sensitivity: 'base' })
                    );
                    setTeachers(sortedTeachers);
                }
            }
            setTeacherLoading(true);
            const res = await $api.studentEvaluation.getEvaluationsForTeacher({
                teacherId: selectedTeacher,
                year: selectedYear,
                semester: selectedSemester
            });
            if (res.result) {
                setTeacherData(res.result);
            }
        } catch (e) {
            message.error("Lỗi tải dữ liệu đánh giá GV");
        } finally {
            setTeacherLoading(false);
        }
    };

    const handleExport = async () => {
        try {
            setExporting(true);
            const res: any = await $api.studentEvaluation.export({
                teacherId: selectedTeacher,
                year: selectedYear,
                semester: selectedSemester
            });
            if (res) {
                fileDownload(res, `Danh_Sach_Danh_Gia_${moment((new Date())).format('DD/MM/YYYY')}.xlsx`);
                message.success("Tải xuống thành công!");
            }
        } catch (e) {
            message.error("Có lỗi khi export dữ liệu");
        } finally {
            setExporting(false);
        }
    };
    useEffect(() => {
        if (!role){
            window.location.href = '/thoi-khoa-bieu';
        }
    }, [role]);

    useEffect(() => {
        if (activeTab === "criteria" && criteriaData.length === 0) {
            fetchCriteria();
        }
        if (activeTab === "gpa" && teacherData.length === 0) {
            fetchTeacherEvaluations();
        }
    }, [activeTab]);

    useEffect(() => {
        if (activeTab === "criteria" ) {
            fetchCriteria();
        }
        if (activeTab === "gpa") {
            fetchTeacherEvaluations();
        }
    }, [filterType, selectedTeacher, selectedSemester, selectedYear]);

    const handleSave = async (values: Evaluation) => {
        try {
            if(values.type && values.type == 0) {
                await $api.evaluation.save({ ...editingItem, ...values });
            }
            else {
                await $api.evaluation.saveDrl({ ...editingItem, ...values });
            }
            message.success("Lưu thành công");
            setIsModalVisible(false);
            form.resetFields();
            fetchCriteria();
        } catch (e: any) {
            let errMsg;
            let type = typeof e?.response?.data?.errors?.exceptionMessage;
            if (type === 'string') {
                errMsg = e?.response?.data?.errors?.exceptionMessage;
            } else if (e?.response?.data?.errors?.exceptionMessage?.title) {
                errMsg = e?.response?.data?.errors?.exceptionMessage?.title;
            } else {
                errMsg = 'Đã có lỗi xảy ra, xin vui lòng thử lại';
            }
            show_ErrMsg(errMsg);
        }
    };

    const handleDelete = async (id?: string, type?: number) => {
        try {
            if (!id) return;
            if (type === 1) {
                await $api.evaluation.deleteDrl(id);
            } else {
                await $api.evaluation.delete(id);
            }
            message.success("Xóa thành công");
            fetchCriteria();
        } catch (e: any) {
            let errMsg;
            let type = typeof e?.response?.data?.errors?.exceptionMessage;
            if (type === 'string') {
                errMsg = e?.response?.data?.errors?.exceptionMessage;
            } else if (e?.response?.data?.errors?.exceptionMessage?.title) {
                errMsg = e?.response?.data?.errors?.exceptionMessage?.title;
            } else {
                errMsg = 'Đã có lỗi xảy ra, xin vui lòng thử lại';
            }
            show_ErrMsg(errMsg);
        }
    };
    function show_ErrMsg(msg) {
        setErrDialog({
            isOpen: true
            , subTitle: msg
        });
    }

    const columns = [
        {
            title: "Tên tiêu chí",
            dataIndex: "name",
            key: "name",
            className: "text-justify",
            onHeaderCell: () => ({
                className: "text-left",
            }),
            render: (text: string, record: Evaluation) => (
                <span className="whitespace-pre-line break-words break-words"
                      style={{ fontWeight: record.children?.length ? 'bold' : 'normal' }}>
                  {text}
                </span>
            )
        },
        {
            title: "Mô tả",
            dataIndex: "description",
            key: "description",
            width: 300,
            className: "text-justify",
            onHeaderCell: () => ({
                className: "text-left",
            }),
            render: (text: string) => (
                <div className="whitespace-pre-line">{text}</div>
            ),
        },
        {
            title: "Loại",
            dataIndex: "type",
            key: "type",
            width: 120,
            className: "text-center",
            onHeaderCell: () => ({ className: "text-center" }),
            render: (val: number) => (val === 0 ? "GPA" : "ĐRL"),
        },
        {
            title: "Điểm",
            dataIndex: "score",
            key: "score",
            width: 100,
            className: "text-center",
            onHeaderCell: () => ({
                className: "text-center",
            }),
        },
        {
            title: "Hành động",
            key: "actions",
            width: 180,
            className: "text-center",
            onHeaderCell: () => ({
                className: "text-center",
            }),
            render: (_: any, record: Evaluation) => (
                <div className="flex justify-center gap-2">
                    <Button
                        className="focus:outline-none focus:ring-0"
                        type="link"
                        onClick={() => {
                            setEditingItem(record);
                            form.setFieldsValue(record);
                            setIsModalVisible(true);
                        }}
                    >
                        <EditOutlined />
                    </Button>
                    <Popconfirm
                        title="Bạn có chắc muốn xóa?"
                        onConfirm={() => handleDelete(record.id, record.type)}
                    >
                        <Button className="focus:outline-none focus:ring-0" type="link" danger>
                            <DeleteOutlined />
                        </Button>
                    </Popconfirm>
                </div>
            ),
        },
    ];

    const teacherColumns = [
        {
            title: "TT",
            render: (_: any, __: TeacherEvaluation, index: number) => index + 1,
            width: 50
        },
        {
            title: "Tên GV",
            dataIndex: "teacherName",
            render: (text: string) => (
                <div className="text-left whitespace-pre-line">{text}</div>
            ),
        },
        {
            title: "Tên môn | HP",
            key: "subject",
            align: "left",
            width: 280,
            render: (_: any, record: TeacherEvaluation) => (
                <div className="whitespace-pre-line break-words leading-tight">
                    <div className="font-medium">{record.subjectName}</div>
                    <div className="text-gray-500 text-sm">{record.subjectTeachingName}</div>
                </div>
            ),
        },
        {
            title: "4",
            dataIndex: "avgFourPoint",
            render: (val: number) => `${val}%`,
            align: "center"
        },
        {
            title: "3",
            dataIndex: "avgThreePoint",
            render: (val: number) => `${val}%`,
            align: "center"
        },
        {
            title: "2",
            dataIndex: "avgTwoPoint",
            render: (val: number) => `${val}%`,
            align: "center"
        },
        {
            title: "1",
            dataIndex: "avgOnePoint",
            render: (val: number) => `${val}%`,
            align: "center"
        },
        {
            title: "AVG",
            dataIndex: "avg",
            align: "center"
        },
        {
            title: "Số SV",
            dataIndex: 'studentCount',
            render: (_: any, record: TeacherEvaluation) => (
                <Badge
                    count={`${record.evaluationCount}/${record.studentCount}`}
                    style={{
                        backgroundColor: record.evaluationCount === record.studentCount ? '#52c41a' : '#1890ff',
                        color: '#fff',
                        fontSize: 13,
                        padding: '0 10px',
                        borderRadius: 9999,
                    }}
                />
            ),
            align: 'center'
        },
        {
            title: "Hành động",
            align: "center",
            render: (_: any, record: TeacherEvaluation) => (
                <Button
                    type="link"
                    icon={<EyeOutlined />}
                    className="focus:outline-none focus:ring-0"
                    onClick={() => {
                        setDetailRecord(record);
                        setDetailModalVisible(true);
                    }}
                />
            )
        }
    ];

    return (
        <DefaultLayout>
            <div className='page__header mb-3'>
                <div className='___title flex items-center'>
                    <div className='___title__decorate' />
                    <div className='___title__box'>
                        <div>Quản lý đánh giá</div>
                    </div>
                </div>
            </div>
            <div className="p-4 bg-white rounded-md shadow ">
                <Tabs activeKey={activeTab} onChange={setActiveTab}>
                    <TabPane tab="Tiêu chí" key="criteria">
                        <div className="my-3 flex justify-start">
                            <Button
                                className="mr-3 flex items-center focus:outline-none focus:ring-0"
                                type="primary"
                                onClick={() => {
                                    setEditingItem(null);
                                    form.resetFields();
                                    setIsModalVisible(true);
                                }}
                            >
                                <i className="fa fa-plus mr-1"></i>
                                Thêm tiêu chí
                            </Button>
                            <Select
                                allowClear
                                placeholder="-- Lọc loại --"
                                className="w-48"
                                value={filterType ?? undefined}
                                onChange={(val) => {
                                    setFilterType(val ?? null);
                                    setPageIndex(1);
                                }}
                            >
                                <Option value={0}>GPA</Option>
                                <Option value={1}>ĐRL</Option>
                            </Select>

                        </div>
                        <Table
                            rowKey="id"
                            columns={columns}
                            dataSource={criteriaData}
                            loading={loading}
                            scroll={{ x: "max-content" }}
                            expandable={{
                                defaultExpandAllRows: true, // mở toàn bộ cấp con
                                expandRowByClick: true,     // click vào hàng để mở rộng
                                rowExpandable: (record) => record.children && record.children.length > 0,
                            }}
                            pagination={{
                                current: pageIndex,
                                pageSize: pageSize,
                                total: totalRecord,
                                onChange: (page, size) => {
                                    setPageIndex(page);
                                    setPageSize(size || 15);
                                    fetchCriteria(page, size || 15);
                                },
                            }}
                        />
                    </TabPane>
                    <TabPane tab="GPA" key="gpa">
                        <div className="my-3 flex gap-3">
                            <Select
                                showSearch
                                placeholder="-- Chọn GV --"
                                className="w-64"
                                allowClear
                                value={selectedTeacher ?? undefined}
                                onChange={(val) => setSelectedTeacher(val ?? null)}
                                optionFilterProp="children"
                                filterOption={(input, option) =>
                                    (option?.children as string)
                                        ?.toLowerCase()
                                        .includes(input.toLowerCase())
                                }
                            >
                                {teachers.map((t) => (
                                    <Option key={t.id} value={t.id}>
                                        {t.fullName}
                                    </Option>
                                ))}
                            </Select>


                            <ConfigProvider locale={viVN}>
                                <DatePicker
                                    picker="year"
                                    className="w-40"
                                    placeholder="Chọn năm học"
                                    value={selectedYear ? dayjs(selectedYear, "YYYY") : null}
                                    onChange={(date) => {
                                        setSelectedYear(date ? date.format("YYYY") : null);
                                        setSelectedSemester(null);
                                    }}
                                    format={(value) => {
                                        if (!value) return "";
                                        const year = value.year();
                                        return `${year} - ${year + 1}`;
                                    }}
                                />
                            </ConfigProvider>

                            <Select
                                placeholder="-- Chọn học kỳ --"
                                className="w-40"
                                allowClear
                                value={selectedSemester ?? undefined}
                                onChange={(val) => setSelectedSemester(val ?? null)}
                                disabled={!selectedYear}
                            >
                                <Option value={1}>Học kỳ 1</Option>
                                <Option value={2}>Học kỳ 2</Option>
                            </Select>

                            <Button
                                type="primary"
                                loading={exporting}
                                className="ml-auto focus:outline-none focus:ring-0"
                                onClick={handleExport}
                            >
                                <i className="fa fa-download mr-2"></i> Tải xuống
                            </Button>

                        </div>

                        <Table
                            rowKey="subjectTeachingId"
                            columns={teacherColumns}
                            dataSource={teacherData}
                            loading={teacherLoading}
                            scroll={{ x: "max-content" }}
                            pagination={false}
                        />

                        <Modal
                            title={<h2 className="text-xl font-semibold">Chi tiết đánh giá</h2>}
                            visible={detailModalVisible}
                            onCancel={() => setDetailModalVisible(false)}
                            footer={null}
                            width={800}
                        >
                            {detailRecord && (
                                <>
                                    <div className="mb-3">
                                        <p><strong>Tên GV:</strong> {detailRecord.teacherName}</p>
                                        <p><strong>Tên môn:</strong> {detailRecord.subjectName} - {detailRecord.subjectTeachingName}</p>
                                        <p><strong>SV đánh giá:</strong> {detailRecord.evaluationCount}/{detailRecord.studentCount}</p>

                                    </div>

                                    <Table
                                        rowKey="evaluationName"
                                        dataSource={detailRecord.evaluationDetails}
                                        scroll={{ x: "max-content" }}
                                        pagination={false}
                                        columns={[
                                            {
                                                title: "Tên tiêu chí",
                                                dataIndex: "evaluationName",
                                                render: (text: string) => (
                                                    <div className="text-justify whitespace-pre-line">{text}</div>
                                                )
                                            },
                                            {
                                                title: "4",
                                                dataIndex: "fourPointCount",
                                                align: "center"
                                            },
                                            {
                                                title: "3",
                                                dataIndex: "threePointCount",
                                                align: "center"
                                            },
                                            {
                                                title: "2",
                                                dataIndex: "twoPointCount",
                                                align: "center"
                                            },
                                            {
                                                title: "1",
                                                dataIndex: "onePointCount",
                                                align: "center"
                                            },
                                            {
                                                title: "AVG",
                                                dataIndex: "avg",
                                                align: "center"
                                            }
                                        ]}
                                        summary={pageData => {
                                            let total4 = 0;
                                            let total3 = 0;
                                            let total2 = 0;
                                            let total1 = 0;
                                            let totalAvg = 0;

                                            pageData.forEach(item => {
                                                total4 += item.fourPointCount;
                                                total3 += item.threePointCount;
                                                total2 += item.twoPointCount;
                                                total1 += item.onePointCount;
                                                totalAvg += item.avg;
                                            });

                                            const avgSummary = detailRecord.avg;

                                            return (
                                                <Table.Summary.Row>
                                                    <Table.Summary.Cell index={0}><strong>Tổng</strong></Table.Summary.Cell>
                                                    <Table.Summary.Cell index={1} align="center"><strong>{total4}</strong></Table.Summary.Cell>
                                                    <Table.Summary.Cell index={2} align="center"><strong>{total3}</strong></Table.Summary.Cell>
                                                    <Table.Summary.Cell index={3} align="center"><strong>{total2}</strong></Table.Summary.Cell>
                                                    <Table.Summary.Cell index={4} align="center"><strong>{total1}</strong></Table.Summary.Cell>
                                                    <Table.Summary.Cell index={5} align="center"><strong>{avgSummary}</strong></Table.Summary.Cell>
                                                </Table.Summary.Row>
                                            );
                                        }}
                                    />

                                    <div className="mt-4">
                                        <strong>Ý kiến sinh viên:</strong>
                                        {detailRecord.studentComments.length > 0 ? (
                                            <ol className="list-decimal pl-6 mt-2 space-y-1">
                                                {detailRecord.studentComments.map((cmt, idx) => (
                                                    <li key={idx}>
                                                        <div className="whitespace-pre-line">{cmt}</div>
                                                    </li>
                                                ))}
                                            </ol>
                                        ) : (
                                            <p>Không có ý kiến.</p>
                                        )}
                                    </div>
                                </>
                            )}
                        </Modal>
                    </TabPane>
                    <TabPane tab="Điểm rèn luyện" key="training">
                        <p>Comming soon, ...</p>
                    </TabPane>
                </Tabs>

                <Modal
                    title={editingItem ? "Sửa tiêu chí" : "Thêm tiêu chí"}
                    visible={isModalVisible}
                    onCancel={() => setIsModalVisible(false)}
                    onOk={() => form.submit()}
                    destroyOnClose
                >
                    <Form form={form} layout="vertical" onFinish={handleSave}>
                        <Form.Item
                            name="name"
                            label="Tên tiêu chí"
                            rules={[{ required: true, message: "Vui lòng nhập tên tiêu chí" }]}
                            getValueFromEvent={(e) => e.target.value.replace(/\r?\n/g, "\n")}
                        >
                            <Input.TextArea rows={3} />
                        </Form.Item>
                        <Form.Item
                            name="type"
                            label="Loại tiêu chí"
                            rules={[{ required: true, message: "Vui lòng chọn loại tiêu chí" }]}
                            initialValue={1}
                        >
                            <Select
                                placeholder="-- Chọn loại --"
                                onChange={(val) => {
                                    form.setFieldValue("type", val);
                                }}
                            >
                                <Option value={1}>ĐRL</Option>
                                <Option value={0}>GPA</Option>
                            </Select>
                        </Form.Item>

                        {/* Nếu là ĐRL mới hiển thị Description */}
                        {form.getFieldValue("type") === 1 && (
                            <Form.Item
                                name="description"
                                label="Mô tả"
                            >
                                <Input.TextArea rows={4} />
                            </Form.Item>
                        )}

                        <Form.Item
                            name="score"
                            label="Điểm"
                            initialValue={0}>
                            <Input type="number" />
                        </Form.Item>

                        {/* Nếu là ĐRL thì cho phép Children */}
                        {form.getFieldValue("type") === 1 && (
                            <Form.List name="children">
                                {(fields, { add, remove }) => (
                                    <>
                                        <div className="flex justify-between items-center mb-3">
                                            <span className="text-lg font-semibold text-gray-700">Tiêu chí con</span>
                                            <Button
                                                type="primary"
                                                onClick={() => add({}, 0)}
                                                className="transition-all duration-300 focus:outline-none focus:ring-0"
                                            >
                                                + Thêm tiêu chí con
                                            </Button>
                                        </div>

                                        {fields.map((field, index) => (
                                            <div
                                                key={field.key}
                                                className="p-4 mb-3 border rounded-xl bg-gradient-to-r from-gray-50 to-gray-100 shadow-sm transition-transform duration-300 hover:scale-[1.01]"
                                            >
                                                <Form.Item
                                                    {...field}
                                                    name={[field.name, "name"]}
                                                    fieldKey={[field.fieldKey, "name"]}
                                                    label="Tên"
                                                    rules={[{ required: true, message: "Nhập tên tiêu chí con" }]}
                                                >
                                                    <Input />
                                                </Form.Item>

                                                <Form.Item
                                                    {...field}
                                                    name={[field.name, "description"]}
                                                    fieldKey={[field.fieldKey, "description"]}
                                                    label="Mô tả"
                                                >
                                                    <Input.TextArea rows={2} />
                                                </Form.Item>

                                                <Form.Item
                                                    {...field}
                                                    name={[field.name, "score"]}
                                                    fieldKey={[field.fieldKey, "score"]}
                                                    label="Điểm"
                                                    rules={[{ required: true, message: "Nhập điểm" }]}
                                                >
                                                    <Input type="number" />
                                                </Form.Item>

                                                <Form.Item
                                                    {...field}
                                                    name={[field.name, "type"]}
                                                    initialValue={1}
                                                    hidden
                                                >
                                                    <Input type="hidden" />
                                                </Form.Item>

                                                <div className="flex justify-end">
                                                    <Button
                                                        type="link"
                                                        danger
                                                        onClick={() => remove(field.name)}
                                                        className="hover:underline focus:outline-none focus:ring-0"
                                                    >
                                                        Xóa tiêu chí con
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                    </>
                                )}
                            </Form.List>

                        )}
                    </Form>
                </Modal>
            </div>
            <ErrorDialog
                errorDialog={errDialog}
                setErrorDialog={setErrDialog}
            />
        </DefaultLayout>
    );
};

export default EvaluationPage;
